/**
 * Wishlist Manager - Account-based Wishlist System
 * Requires customer login for wishlist functionality
 */

class WishlistManager {
  constructor() {
    this.storageKey = 'shopify_wishlist';
    this.customerId = window.shopifyCustomerId || null;
    this.isLoggedIn = !!this.customerId;
    this.wishlist = this.loadWishlist();
    this.init();
  }

  init() {
    this.updateAllWishlistButtons();
    this.updateWishlistCount();
    this.bindEvents();
  }

  getStorageKey() {
    return this.isLoggedIn ? `${this.storageKey}_${this.customerId}` : this.storageKey;
  }

  loadWishlist() {
    try {
      const stored = localStorage.getItem(this.getStorageKey());
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('Wishlist load error:', e);
      return [];
    }
  }

  saveWishlist() {
    try {
      localStorage.setItem(this.getStorageKey(), JSON.stringify(this.wishlist));
    } catch (e) {
      console.error('Wishlist save error:', e);
    }
  }

  addItem(productId, productHandle) {
    if (!this.isLoggedIn) {
      this.redirectToLogin();
      return false;
    }

    const item = { id: productId, handle: productHandle, addedAt: Date.now() };
    
    if (!this.isInWishlist(productId)) {
      this.wishlist.push(item);
      this.saveWishlist();
      this.updateAllWishlistButtons();
      this.updateWishlistCount();
      this.showNotification('added');
      return true;
    }
    return false;
  }

  removeItem(productId) {
    if (!this.isLoggedIn) {
      this.redirectToLogin();
      return false;
    }

    const index = this.wishlist.findIndex(item => item.id == productId);
    if (index > -1) {
      this.wishlist.splice(index, 1);
      this.saveWishlist();
      this.updateAllWishlistButtons();
      this.updateWishlistCount();
      this.showNotification('removed');
      return true;
    }
    return false;
  }

  toggleItem(productId, productHandle) {
    if (!this.isLoggedIn) {
      this.redirectToLogin();
      return;
    }

    if (this.isInWishlist(productId)) {
      this.removeItem(productId);
    } else {
      this.addItem(productId, productHandle);
    }
  }

  isInWishlist(productId) {
    return this.wishlist.some(item => item.id == productId);
  }

  getWishlistCount() {
    return this.wishlist.length;
  }

  getWishlist() {
    return this.wishlist;
  }

  clearWishlist() {
    this.wishlist = [];
    this.saveWishlist();
    this.updateAllWishlistButtons();
    this.updateWishlistCount();
  }

  redirectToLogin() {
    const currentUrl = window.location.href;
    const loginUrl = window.routes?.account_login_url || '/account/login';
    window.location.href = `${loginUrl}?return_url=${encodeURIComponent(currentUrl)}`;
  }

  updateAllWishlistButtons() {
    document.querySelectorAll('[data-wishlist-btn]').forEach(btn => {
      const productId = btn.dataset.productId;
      const isInWishlist = this.isInWishlist(productId);
      
      btn.classList.toggle('is-active', isInWishlist);
      btn.setAttribute('aria-pressed', isInWishlist);
      
      // Update icon if exists
      const outlineIcon = btn.querySelector('.icon-wishlist--outline');
      const filledIcon = btn.querySelector('.icon-wishlist--filled');
      
      if (outlineIcon && filledIcon) {
        outlineIcon.style.display = isInWishlist ? 'none' : 'block';
        filledIcon.style.display = isInWishlist ? 'block' : 'none';
      }
    });
  }

  updateWishlistCount() {
    const count = this.getWishlistCount();
    
    document.querySelectorAll('[data-wishlist-count]').forEach(el => {
      el.textContent = count;
      el.closest('.wishlist-count')?.toggleAttribute('hidden', count === 0);
    });

    // Update header icon badge
    const headerCount = document.querySelector('.header-wishlist-count');
    if (headerCount) {
      headerCount.textContent = count < 100 ? count : '99+';
      headerCount.toggleAttribute('hidden', count === 0);
    }
  }

  showNotification(action) {
    const messages = {
      added: window.wishlistStrings?.added || 'Added to wishlist',
      removed: window.wishlistStrings?.removed || 'Removed from wishlist'
    };

    // Use existing notification system if available
    if (typeof window.showToast === 'function') {
      window.showToast(messages[action]);
    } else {
      // Simple notification fallback
      this.showSimpleNotification(messages[action]);
    }
  }

  showSimpleNotification(message) {
    const existing = document.querySelector('.wishlist-notification');
    if (existing) existing.remove();

    const notification = document.createElement('div');
    notification.className = 'wishlist-notification';
    notification.innerHTML = `
      <div class="wishlist-notification__content">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
        </svg>
        <span>${message}</span>
      </div>
    `;
    document.body.appendChild(notification);

    requestAnimationFrame(() => {
      notification.classList.add('is-visible');
    });

    setTimeout(() => {
      notification.classList.remove('is-visible');
      setTimeout(() => notification.remove(), 300);
    }, 2500);
  }

  bindEvents() {
    // Delegate click events for wishlist buttons
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-wishlist-btn]');
      if (btn) {
        e.preventDefault();
        e.stopPropagation();
        
        const productId = btn.dataset.productId;
        const productHandle = btn.dataset.productHandle;
        
        this.toggleItem(productId, productHandle);
      }
    });

    // Handle remove from wishlist page
    document.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('[data-wishlist-remove]');
      if (removeBtn) {
        e.preventDefault();
        const productId = removeBtn.dataset.productId;
        this.removeItem(productId);
        
        // Remove the card from DOM on wishlist page
        const card = removeBtn.closest('.wishlist-item');
        if (card) {
          card.style.opacity = '0';
          card.style.transform = 'scale(0.9)';
          setTimeout(() => {
            card.remove();
            this.checkEmptyWishlist();
          }, 300);
        }
      }
    });
  }

  checkEmptyWishlist() {
    const container = document.querySelector('.wishlist-products');
    const emptyState = document.querySelector('.wishlist-empty');
    
    if (container && emptyState && this.getWishlistCount() === 0) {
      container.style.display = 'none';
      emptyState.style.display = 'block';
    }
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  window.wishlistManager = new WishlistManager();
});

// Export for use in other scripts
window.WishlistManager = WishlistManager;

